import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ClinicalPrescriptionPd, PdType } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/LoadingState';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/format';
import { useCustomerMeasurementsQuery } from '@/modules/measurements/api/measurements.api';
import { isPrescribablePd } from '../pdEligibility';

interface PdMeasurementPickerProps {
  customerId: string;
  value: string;
  onChange: (measurementId: string) => void;
  canViewMeasurements: boolean;
  error?: string;
}

/**
 * Choosing the PD a prescription carries, from the customer's own measurement history.
 *
 * No PD is entered or edited here. Camera estimates and validation data are listed, plainly marked and not selectable; a new PD is recorded on the customer's profile, where measurements live.
 */
export function PdMeasurementPicker({
  customerId,
  value,
  onChange,
  canViewMeasurements,
  error,
}: PdMeasurementPickerProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const measurements = useCustomerMeasurementsQuery(canViewMeasurements ? customerId : undefined);

  const withPd = (measurements.data ?? []).filter((measurement) => measurement.pdType !== null);
  const linked = withPd.find((measurement) => measurement.id === value);
  // A linked measurement voided since, or no longer listed, cannot go on the prescription: say so before a save is refused.
  const linkedIsUsable = value === '' || (linked !== undefined && isPrescribablePd(linked));

  return (
    <fieldset className="space-y-3">
      <legend className="field-label">{t('medical.pd.title')}</legend>
      <p className="text-muted text-xs">{t('medical.pd.hint')}</p>

      {!canViewMeasurements ? (
        <Alert tone="info">{t('medical.pd.noPermission')}</Alert>
      ) : measurements.isPending ? (
        <LoadingState />
      ) : (
        <ul className="border-line divide-line divide-y rounded-lg border">
          <li className="flex items-center gap-3 px-3 py-2.5">
            <input
              id={`pd-none-${customerId}`}
              type="radio"
              name={`pd-${customerId}`}
              checked={value === ''}
              onChange={() => onChange('')}
            />
            <label htmlFor={`pd-none-${customerId}`} className="text-ink text-sm">
              {t('medical.pd.none')}
            </label>
          </li>
          {withPd.map((measurement) => {
            const selectable = isPrescribablePd(measurement);
            const inputId = `pd-${measurement.id}`;
            return (
              <li key={measurement.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <input
                  id={inputId}
                  type="radio"
                  name={`pd-${customerId}`}
                  checked={value === measurement.id}
                  disabled={!selectable}
                  onChange={() => onChange(measurement.id)}
                />
                <label htmlFor={inputId} className="min-w-0 flex-1 text-sm">
                  <PdFigures
                    right={measurement.rightPdMm}
                    left={measurement.leftPdMm}
                    total={measurement.totalPdMm}
                  />
                  <span className="text-muted block text-xs">
                    {measurement.pdType
                      ? `${t(`measurements.pdTypeTag.${measurement.pdType}`)} · `
                      : ''}
                    {formatDate(measurement.measuredAt, locale)}
                    {measurement.measuredByName ? ` · ${measurement.measuredByName}` : ''}
                  </span>
                </label>
                {measurement.referenceOnly ? (
                  <Badge tone="warning">{t('measurements.camera.badge')}</Badge>
                ) : null}
                {measurement.isValidation ? (
                  <Badge tone="warning">{t('measurements.validation')}</Badge>
                ) : null}
                {!selectable ? (
                  <span className="text-muted text-xs">{t('medical.pd.notSelectable')}</span>
                ) : null}
              </li>
            );
          })}
          {withPd.length === 0 ? (
            <li className="text-muted px-3 py-2.5 text-sm">{t('medical.pd.empty')}</li>
          ) : null}
        </ul>
      )}

      {canViewMeasurements && !measurements.isPending && !linkedIsUsable ? (
        <Alert tone="warning">{t('medical.pd.unavailable')}</Alert>
      ) : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <Link
        to={`/customers/${customerId}`}
        className="text-brand-600 hover:text-brand-700 text-sm font-medium"
      >
        {t('medical.pd.record')}
      </Link>
    </fieldset>
  );
}

/** A frozen or linked PD, read-only: the figures it carries, its type, and whether its source has since been voided. */
export function PdSnapshot({ pd, frozen }: { pd: ClinicalPrescriptionPd; frozen: boolean }) {
  const { t } = useTranslation();
  if (!pd.measurementId && !pd.totalPdMm && !pd.rightPdMm && !pd.leftPdMm) {
    return <p className="text-muted text-sm">{t('medical.pd.none')}</p>;
  }
  return (
    <div className="space-y-1">
      <PdFigures right={pd.rightPdMm} left={pd.leftPdMm} total={pd.totalPdMm} />
      <p className="text-muted text-xs">
        {pd.pdType ? `${t(`measurements.pdTypeTag.${pd.pdType as PdType}`)}` : ''}
        {frozen ? ` · ${t('medical.pd.frozen')}` : ''}
      </p>
      {pd.measurementVoided ? <Badge tone="neutral">{t('medical.pd.sourceVoided')}</Badge> : null}
    </div>
  );
}

/** The PD figures as one left-to-right row in either language, R before L, like the clinical grids. */
function PdFigures({
  right,
  left,
  total,
}: {
  right: string | null;
  left: string | null;
  total: string | null;
}) {
  const { t } = useTranslation();
  return (
    <span dir="ltr" className="text-ink flex flex-wrap items-baseline gap-x-3 font-medium">
      {right ? (
        <span dir="ltr" className="font-mono tabular-nums">
          R {right} mm
        </span>
      ) : null}
      {left ? (
        <span dir="ltr" className="font-mono tabular-nums">
          L {left} mm
        </span>
      ) : null}
      {total ? (
        <span>
          <bdi>{t('medical.pd.total')}</bdi>{' '}
          <span dir="ltr" className="font-mono tabular-nums">
            PD {total} mm
          </span>
        </span>
      ) : null}
    </span>
  );
}
